import * as am5 from "@amcharts/amcharts5";
import { FormControlLabel, Radio, RadioGroup, useTheme } from '@mui/material';
import EADialog from 'components/common/EADialog';
import EAIcon from 'components/common/EAIcon';
import EAAccordion from 'components/EAAccordion';
import i18next from 'i18next';
import { Trans } from 'react-i18next';
import { SecurityRatingGlobalScoreMaturityEnum, SecurityRatingIpProviders } from 'services/common/SecurityRatingUtils';
import useStyles from 'style/js-style/components/thirdparties/securityRating/SecurityRatingStyle';

import clsx from 'clsx';
import EAPieChart from 'components/common/charts/EAPieChart';
import { useEffect, useMemo, useState } from 'react';
import { hidePieSliceLabel } from 'services/common/ChartUtils';
import { formatNumber } from 'services/common/Utils';
import EATooltip from "components/common/EATooltip";

const getLeftPosition = (value) => {

  let position;
  if (value <= 500) {
    position = (value / 500) * 33;
  } else if (value <= 750) {
    position = 33 + ((value - 500) / 250) * 33;
  } else {
    position = 66 + ((value - 750) / 250) * 33;
  }

  position = Math.max(2, Math.min(96, position));

  return `${position}%`
};

const ScaleTooltip = () => {
  return (
    <>
      <div><Trans i18nKey={"securityRating.cyberMaturityModal.scaleTooltip1"}>X<b>Y</b>Z</Trans></div>
      <div><Trans i18nKey={"securityRating.cyberMaturityModal.scaleTooltip2"}>X<b>Y</b>Z</Trans></div>
    </>
  )
}

const PlainScale = ({ values, label }) => {
  const { classes } = useStyles();

  return (
    <div className={classes.scaleWrapper}>
      <div className={classes.scaleLabel}>
        {i18next.t(`securityRating.cyberMaturityModal.${label}`)}
        <EATooltip
          title={<ScaleTooltip />}
          placement="top"
          arrow
        >
          <div>
            <EAIcon icon="info-rounded" />
          </div>
        </EATooltip>
      </div>
      <div className={classes.plainScale}>
        {Object.values(values)?.map(item => (
          <div className={classes.plainScaleValue} style={{ left: getLeftPosition(item.value) }} key={item.maturity}>{item.value}</div>
        ))}
      </div>
    </div>
  )
}

const ColoredScale = ({ value }) => {

  const { classes } = useStyles();

  return (
    <div className={classes.scaleWrapper}>
      <div className={classes.scaleLabel}>
        {i18next.t(`securityRating.maturity.label`)}
      </div>
      <div className={classes.coloredScaleWrapper}>
        {Object.values(SecurityRatingGlobalScoreMaturityEnum).map(maturity => (
          <div className={clsx(classes.coloredScaleSection, maturity)} key={maturity}>{i18next.t(`securityRating.maturity.${maturity}`)}</div>
        ))}
        <div className={classes.scaleRangeWrapper}>
          {[0, 500, 750, 1000].map(range => (
            <div className={clsx(classes.scaleRange, `range-${range}`)} key={range}>{range}</div>
          ))}
        </div>
        <div className={classes.scaleScoreContainer}>
          <div className={classes.scaleScoreWrapper} style={{ left: getLeftPosition(value) }}>
            <div className={classes.scaleScoreBubble} >{value}</div>
            <div className={classes.scaleScoreLine}></div>
          </div>
        </div>
      </div>
    </div>
  )
};

const getIpProviderGraphColor = (index, theme) => {
  return [
    theme.palette.primary.main,
    theme.palette.warning.warning400,
    theme.palette.warning.warning600,
    theme.palette.error.error400
  ][index];
}

const SecurityRatingCyberMaturityModal = ({ data, isOpen = false, onClose = () => { } }) => {
  const { classes } = useStyles();
  const theme = useTheme();

  const [ipProviderTypeSelected, setIpProviderTypeSelected] = useState(null);

  // Delays the selected ip provider type to avoid labels going out of graph.
  useEffect(() => {
    const timer = setTimeout(() => {
      setIpProviderTypeSelected(SecurityRatingIpProviders.THIRDPARTY);
    }, 100);

    return () => {
      clearTimeout(timer);
      setIpProviderTypeSelected(null);
    };
  }, [isOpen]);

  const selectedIpProviderTypeChartData = useMemo(() => {
    let ipProviderTypeKey = 'ip_providers';
    if (ipProviderTypeSelected !== SecurityRatingIpProviders.THIRDPARTY) {
      ipProviderTypeKey = `${SecurityRatingIpProviders[ipProviderTypeSelected]?.toLowerCase()}_ip_providers`
    }

    const orderedProvidersData = data[ipProviderTypeKey]?.sort((a, b) => b.count - a.count) || [];
    let selectedIpProviderTypeData = orderedProvidersData.slice(0, 4)?.map((item, index) => ({ ...item, color: getIpProviderGraphColor(index, theme) }));

    // Group the remaining ip providers in 'Others' object
    const others = orderedProvidersData.slice(4);
    const othersTotal = others.reduce((sum, item) => sum + item.count, 0);
    if (others.length > 0) {
      selectedIpProviderTypeData.push({ provider: i18next.t('others'), count: othersTotal, color: theme.palette.grey.grey700 });
    }

    const total = selectedIpProviderTypeData.map(x => x.count)?.reduce((sum, a) => sum + a, 0);

    return {
      data: selectedIpProviderTypeData
        .map(ipProvider => ({
          id: ipProvider.provider,
          itemLabel: ipProvider.provider,
          value: ipProvider.count,
          formattedValue: formatNumber(ipProvider.count),
          settings: {
            fill: am5.color(ipProvider.color),
          },
          labelSettings: {
            forceHidden: hidePieSliceLabel(ipProvider.count, total),
          },
        }))
        ?.sort((a, b) => a.value >= b.value ? -1 : 1)
      ,
      total: formatNumber(total),
    }
  }, [data, theme, ipProviderTypeSelected]);

  return (
    <EADialog
      isOpen={isOpen}
      maxWidth="md"
      hasActions={false}
      onClose={onClose}
      title={i18next.t("securityRating.cyberMaturityModal.title")}
    >

      {/* Score Scales */}
      <div className={classes.scaleContainer}>
        <div className={classes.scaleTitle}>{i18next.t("securityRating.cyberMaturityModal.scale")}</div>
        <ColoredScale value={data.score.value} />
        <PlainScale values={data.sector_score} label="sector" />
        <PlainScale values={data.global_score} label="global" />
      </div>

      {/* Maturity Informations */}
      <div className={classes.maturityInfosWrapper}>
        {Object.values(SecurityRatingGlobalScoreMaturityEnum).map(maturity => (
          <div className={classes.maturityInfos} key={maturity}><Trans i18nKey={`securityRating.cyberMaturityModal.${maturity}Infos`}>X<b>Y</b>Z</Trans></div>
        ))}
      </div>

      {/* IP Providers Charts */}
      <EAAccordion
        title={i18next.t('securityRating.cyberMaturityModal.ipProviders.title')}
        logo="donut_small"
        opened={false}
        titleNoResult
      >
        <div className={classes.ipProviderGraphContainer}>
          <RadioGroup row name="radio-buttons-ip-providers"
            className={classes.ipProviderRadioButtons}
            value={ipProviderTypeSelected ?? ""}
            onChange={(event) => setIpProviderTypeSelected(event.target.value)}
          >
            {Object.keys(SecurityRatingIpProviders).map(providerType => {
              return (
                <FormControlLabel
                  key={providerType}
                  value={providerType}
                  control={<Radio />}
                  label={i18next.t(`securityRating.cyberMaturityModal.ipProviders.${providerType.toLowerCase()}`)}
                />
              )
            })}
          </RadioGroup>

          <div className={classes.ipProviderGraphWrapper}>
            <EAPieChart
              id={`subscores-${ipProviderTypeSelected}-chart`}
              data={selectedIpProviderTypeChartData}
              tab={"maturity"}
              subTab={ipProviderTypeSelected}
              variant="subscores"
            />
          </div>
        </div>
      </EAAccordion>

    </EADialog>
  )
};

export default SecurityRatingCyberMaturityModal;